/* eslint-disable no-alert */
import React, { useContext, useState } from "react";
import { PropTypes } from 'prop-types';
import { Button, message } from "antd";
import axios from "axios";

import { SubsContext } from "../../components/Billsby/SubscriptionInfo";
import { isFeatureEnabled } from "../../config/featureFlags";
import { apiUrl } from "../../config/properties";
import { makeGraphQLRequest } from "../../utils/requestUtils";
import { getOrgByID } from "../../graphql/organizationDetails";

const AceButton = ({ items, auth }) => {
  const {
    subscriptionLevel,
  } = useContext(SubsContext);
  const [loading, setLoading] = useState(false);

  const addToAceFolder = async (solicitationId, userId) => {
    try {
      // First, get current folders
      const getFoldersQuery = `query user($id: ID) {
        user(id: $id) {
          folders {
            flagged
            tbq
            inprocess
            archived
            ace
            aceDates
          }
        }
      }`;

      const getFoldersResponse = await makeGraphQLRequest({
        query: getFoldersQuery,
        variables: { id: userId },
      });

      const currentFolders = getFoldersResponse.user.folders || {
        flagged: [],
        tbq: [],
        inprocess: [],
        archived: [],
        ace: [],
        aceDates: {},
      };

      // Add solicitation to ACE folder if not already there
      if (!currentFolders.ace) {
        currentFolders.ace = [];
      }
      if (currentFolders.ace.indexOf(solicitationId) === -1) {
        currentFolders.ace.push(solicitationId);
      }

      // Store the date when sent to ACE
      if (!currentFolders.aceDates) {
        currentFolders.aceDates = {};
      }
      // Format date as YYYY-MM-DD
      const today = new Date().toISOString().split('T')[0];
      currentFolders.aceDates[solicitationId] = today;

      // Update folders
      const updateFoldersMutation = `mutation updateFolders($criteria: FoldersInput, $userId: ID) {
        updateFolders(folders: $criteria, userId: $userId) {
          folders {
            ace
            aceDates
          }
        }
      }`;

      await makeGraphQLRequest({
        query: updateFoldersMutation,
        variables: { criteria: currentFolders, userId },
      });

      console.log('Solicitation added to ACE folder');
      console.log('ACE folder contents:', currentFolders.ace);
      console.log('ACE dates:', currentFolders.aceDates);
      console.log('Solicitation ID stored:', solicitationId);
    } catch (error) {
      console.error('Error adding solicitation to ACE folder:', error);
      // Don't show error to user as the main operation (sending to ACE) succeeded
    }
  };

  const sendToAce = async () => {
    if (!isFeatureEnabled('ENABLE_BILLSBY') || subscriptionLevel !== 'ICON Pro') {
      message.error("You must have a Subscription Level of 'ICON Pro' to access this feature.");
      return;
    }

    setLoading(true);

    try {
      // Extract unitCost from procurement history (most recent entry)
      let unitCost = null;
      if (items.procurementHistory && items.procurementHistory.length > 0) {
        // Sort by awardedDate descending to get most recent
        const sortedHistory = [...items.procurementHistory].sort((a, b) => {
          const dateA = new Date(a.awardedDate || 0);
          const dateB = new Date(b.awardedDate || 0);
          return dateB - dateA;
        });
        unitCost = sortedHistory[0].unitCost;
      }

      // Extract standardCost from solicitationTab
      let standardCost = null;
      if (items.solicitationTab && items.solicitationTab.length > 0) {
        // Get the first solicitationTab entry's standardCost
        standardCost = items.solicitationTab[0].standardCost;
      }

      // Helper function to safely extract nested values
      const getValue = (obj, path, defaultValue = '') => {
        if (!obj) return defaultValue;
        const keys = path.split('.');
        const value = keys.reduce((current, key) => {
          if (current && typeof current === 'object') {
            return current[key];
          }
          return defaultValue;
        }, obj);
        return value || defaultValue;
      };

      // Extract EBS Material fields from itemDescInfo (passed by parent component)
      // Note: itemDescInfo from solicitation GraphQL is a String, but parent may pass itemDescription from universal details
      // Check both itemDescInfo (if it's an object/array) and itemDescription (from universal details query)
      let itemDesc = null;
      // First try itemDescInfo if it's an object or array (not a plain string)
      if (items.itemDescInfo && typeof items.itemDescInfo !== 'string') {
        itemDesc = Array.isArray(items.itemDescInfo)
          ? items.itemDescInfo[0]
          : items.itemDescInfo;
      }
      // Fallback to itemDescription from universal details query (contains EBS Material fields)
      if (!itemDesc && items.itemDescription) {
        itemDesc = Array.isArray(items.itemDescription)
          ? items.itemDescription[0]
          : items.itemDescription;
      }

      // Extract itemDetails (can be object or array)
      const itemDetails = Array.isArray(items.itemDetails)
        ? items.itemDetails[0]
        : items.itemDetails;

      // Extract ebsPackaging (can be object or array)
      const ebsPackaging = Array.isArray(items.ebsPackaging)
        ? items.ebsPackaging[0]
        : items.ebsPackaging;

      // Extract NAICS from recentSolicitations (first entry)
      const recentSol = Array.isArray(items.recentSolicitations) && items.recentSolicitations.length > 0
        ? items.recentSolicitations[0]
        : null;

      // Extract NAICS - try multiple sources
      let naicsValue = null;
      if (recentSol) {
        naicsValue = recentSol.NAICS || recentSol.naics || recentSol.NAICS_CODE || recentSol.naicsCode;
      }
      // Fallback to direct items.naics if not found in recentSolicitations
      if (!naicsValue) {
        naicsValue = items.naics || items.NAICS || items.NAICS_CODE || items.naicsCode;
      }
      // Also check purchaseRequest if available
      if (!naicsValue && items.purchaseRequest) {
        naicsValue = items.purchaseRequest.naics || items.purchaseRequest.NAICS || items.purchaseRequest.NAICS_CODE;
      }

      // Extract End Item (ws_name) from wsdcs array
      let endItem = null;
      if (items.wsdcs && Array.isArray(items.wsdcs) && items.wsdcs.length > 0) {
        const firstWSDC = items.wsdcs[0];
        endItem = firstWSDC.wsName || firstWSDC.WS_NAME || null;
      }

      // Extract Part Number from refNbrs (MCRL/MCRD data)
      // Part Number should come from the refNbr field, not purchaseRequest.number (which is a requisition number)
      let partNumber = items.partNumber || '';
      if (!partNumber && items.refNbrs && Array.isArray(items.refNbrs) && items.refNbrs.length > 0) {
        // Get the first refNbr that has a valid refNbr field
        const firstRefNbr = items.refNbrs.find(ref => ref && ref.refNbr);
        if (firstRefNbr) {
          partNumber = firstRefNbr.refNbr;
        }
      }

      // Calculate delivery as number of days between issue date and close date
      // Delivery should be NUMBER OF DAYS, not a date string
      let deliveryDays = null;
      if (items.issueDate && items.closes) {
        const issueDate = new Date(items.issueDate);
        const closeDate = new Date(items.closes);
        if (!Number.isNaN(issueDate.getTime()) && !Number.isNaN(closeDate.getTime())) {
          const timeDiff = closeDate.getTime() - issueDate.getTime();
          deliveryDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
        }
      }
      // If we couldn't calculate days, try to use existing delivery field if it's a number
      if (deliveryDays === null && items.delivery && !Number.isNaN(parseInt(items.delivery, 10))) {
        deliveryDays = parseInt(items.delivery, 10);
      }

      // Extract solicitation data from items
      const solicitationData = {
        solicitationNum: items.solicitationNum || items.id,
        nomenclature: items.nomenclature || (items.purchaseRequest && items.purchaseRequest.nomenclature ? items.purchaseRequest.nomenclature : ''),
        nsn: items.nsn || (items.purchaseRequest && items.purchaseRequest.nsnPart ? items.purchaseRequest.nsnPart : ''),
        // Part Number extracted from refNbrs (MCRL/MCRD data)
        partNumber,
        quantity: items.quantity || (items.purchaseRequest && items.purchaseRequest.quantity ? items.purchaseRequest.quantity : 0),
        // Delivery as NUMBER OF DAYS between issue date and close date
        delivery: deliveryDays,
        um: items.um || '', // Unit of measure for ACE mapping
        estValue: items.estValue || '', // Estimated value for ACE mapping (fallback)
        unitCost, // Unit cost from most recent procurement history
        standardCost, // Standard cost from solicitationTab
        prgid: items.prgid || '', // Program ID if available
        setAside: items.setAside || (items.purchaseRequest && items.purchaseRequest.setAside ? items.purchaseRequest.setAside : ''),
        packagingInfo: items.packagingInfo || '', // Packaging information
        amc: items.amc || '', // AMC information
        procurementHistory: items.procurementHistory || [], // Procurement history
        solicitationTab: items.solicitationTab || [], // SolicitationTab data for standardCost extraction
        techInfo: items.techInfo || '', // Technical information
        // EBS Material fields from EBS_MATERIAL table (itemDescription)
        gfmInd: getValue(itemDesc, 'GFM_IND') || getValue(itemDesc, 'gfmInd'),
        govFrnshPropInd: getValue(itemDesc, 'GOV_FRNSH_PROP_IND') || getValue(itemDesc, 'govFrnshPropInd'),
        flightSftyInd: getValue(itemDesc, 'FLIGHT_SFTY_IND') || getValue(itemDesc, 'flightSftyInd'),
        frstArtclTstInd: getValue(itemDesc, 'FRST_ARTCL_TST_IND') || getValue(itemDesc, 'frstArtclTstInd'),
        prodLotTestingInd: getValue(itemDesc, 'PROD_LOT_TESTING_IND') || getValue(itemDesc, 'prodLotTestingInd'),
        expCntrlInd: getValue(itemDesc, 'EXP_CNTRL_IND') || getValue(itemDesc, 'expCntrlInd'),
        rghtDataQplCd: getValue(itemDesc, 'RGHT_DATA_QPL_CD') || getValue(itemDesc, 'rghtDataQplCd'),
        brgInd: getValue(itemDesc, 'BRG_IND') || getValue(itemDesc, 'brgInd'),
        ctrlTdpInd: getValue(itemDesc, 'CTRL_TDP_IND') || getValue(itemDesc, 'ctrlTdpInd'),
        resTpdInd: getValue(itemDesc, 'RES_TPD_IND') || getValue(itemDesc, 'resTpdInd'),
        // HMIC_0865 from NSN_MASTER table (itemDetails)
        hmic0865: getValue(itemDetails, 'HMIC_0865') || getValue(itemDetails, 'hmic0865') || items.hmic0865,
        // PLACE_INSPN_CD from EBS_PACKAGING table
        placeInspnCd: getValue(ebsPackaging, 'PLACE_INSPN_CD') || getValue(ebsPackaging, 'placeInspnCd') || items.placeInspnCd,
        // NAICS from EBS_PRCH_REQ table (recentSolicitations) - improved extraction
        naics: naicsValue || '',
        // Additional solicitation fields
        issueDate: items.issueDate || '', // Issue date
        closes: items.closes || items.returnDate || '', // Close date
        fob: items.deliveryByFobDestination || items.fob || items.delivery_by_fob_destination || null, // FOB destination
        endItem: endItem || '', // End Item from WSDC ws_name
        castingType: items.castingType || '', // Casting Type (Forging or Casting)
        srva: items.srva || '', // SRVA information
        refNbrs: items.refNbrs || [], // Reference numbers (MCRL/MCRD) - Part Number Seg C
      };

      // Debug logging for data extraction
      console.log('=== ACE DATA EXTRACTION DEBUG ===');
      console.log('Full items object:', items);
      console.log('um from items:', items.um);
      console.log('um type:', typeof items.um);
      console.log('estValue from items:', items.estValue);
      console.log('estValue type:', typeof items.estValue);
      console.log('estValue length:', items.estValue ? items.estValue.length : 'undefined');
      console.log('unitCost extracted:', unitCost);
      console.log('unitCost type:', typeof unitCost);
      console.log('standardCost extracted:', standardCost);
      console.log('standardCost type:', typeof standardCost);
      console.log('procurementHistory length:', items.procurementHistory ? items.procurementHistory.length : 'undefined');
      console.log('solicitationTab length:', items.solicitationTab ? items.solicitationTab.length : 'undefined');
      console.log('NAICS extraction:', {
        recentSol,
        naicsFromRecentSol: recentSol ? (recentSol.NAICS || recentSol.naics) : null,
        naicsFromItems: items.naics || items.NAICS,
        naicsFromPurchaseRequest: items.purchaseRequest ? (items.purchaseRequest.naics || items.purchaseRequest.NAICS) : null,
        finalNaicsValue: naicsValue
      });
      console.log('All items keys:', Object.keys(items));
      console.log('Extracted solicitation data:', solicitationData);
      console.log('=== END ACE DEBUG ===');

      // Validate and prepare user/org IDs
      const userId = auth.id;
      const organizationId = auth.organizationId;
      const userEmail = auth.email;
      const userName = auth.name;
      const cageCode = auth.cageCode;

      // Check if we have valid user data
      if (!userId) {
        message.error('User ID is required to send solicitation to ACE.');
        return;
      }

      if (!organizationId) {
        message.error('Organization ID is required to send solicitation to ACE.');
        return;
      }

      // Parse user name into first and last name for Salesforce provisioning
      let contactFirstName = '';
      let contactLastName = '';
      if (userName) {
        const nameParts = userName.trim().split(/\s+/);
        contactFirstName = nameParts[0] || '';
        contactLastName = nameParts.slice(1).join(' ') || nameParts[0] || '';
      }

      // Validate required user fields for Salesforce provisioning
      if (!userEmail) {
        console.warn('User email is missing. Salesforce user provisioning may fail.');
      }

      if (!userName || !contactFirstName || !contactLastName) {
        console.warn('User name is missing or incomplete. Salesforce user provisioning may fail.');
      }

      if (!cageCode) {
        console.warn('CAGE code is missing. Salesforce account provisioning may fail.');
      }

      // Fetch organization name from database
      let accountName = organizationId; // Fallback to organizationId if name not found
      try {
        console.log(`🔍 Fetching organization name for ID: ${organizationId}`);
        const orgData = await getOrgByID(organizationId);
        if (orgData && orgData.organization && orgData.organization.name) {
          accountName = orgData.organization.name;
          console.log(`✅ Found organization name: "${accountName}"`);
        } else {
          console.warn(`⚠️ Organization name not found for ID: ${organizationId}, using fallback`);
        }
      } catch (orgError) {
        console.error(`❌ Error fetching organization name:`, orgError);
        console.warn(`⚠️ Could not fetch organization name, using fallback: ${accountName}`);
      }

      // Prepare the request payload
      // Include user/org data for Salesforce user provisioning if needed
      const payload = {
        solicitation: solicitationData,
        prgid: items.prgid || 'DEFAULT-PRGID', // Provide default if missing
        userId,
        organizationId,
        accountName, // Organization name fetched from database
        accountIconId: organizationId, // ICON organization ID
        cage: cageCode || '', // CAGE code
        // User data for Salesforce provisioning (if backend needs to create user)
        user: {
          contactIconId: userId, // ICON user ID
          contactEmail: userEmail || '',
          contactFirstName,
          contactLastName,
        },
        // Organization data for Salesforce account provisioning
        organization: {
          accountIconId: organizationId, // ICON organization ID
          cage: cageCode || '',
          accountName, // Organization name
        },
      };

      // Debug logging for payload
      console.log('ACE Payload Debug:', {
        payload,
        hasSolicitation: !!payload.solicitation,
        hasPrgid: !!payload.prgid,
        hasUserId: !!payload.userId,
        hasOrganizationId: !!payload.organizationId,
        hasUserData: !!payload.user,
        hasOrganizationData: !!payload.organization,
        actualUserId: userId,
        actualOrganizationId: organizationId,
        userEmail,
        userName,
        cageCode,
        naicsInSolicitation: payload.solicitation && payload.solicitation.naics ? payload.solicitation.naics : 'NOT FOUND',
      });

      // Get the API endpoint - use the serverless API, not tomcat host
      const baseApiUrl = apiUrl.replace('/api/', ''); // Remove /api/ suffix to get base URL
      const endpoint = `${baseApiUrl}/ace/send-solicitation`;

      console.log('ACE API Endpoint:', endpoint);

      // Make the API call
      const response = await axios.post(endpoint, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.jwtToken || localStorage.getItem('token')}`,
        },
      });

      console.log('ACE Response:', response.data);
      console.log('Account Name from ACE:', response.data.accountName);
      console.log('Account Icon ID:', response.data.accountIconId);

      if (response.data.success) {
        // Display success message with organization name if available
        const successMessage = response.data.accountName 
          ? `Solicitation sent to ACE successfully! Organization: ${response.data.accountName}`
          : 'Solicitation sent to ACE successfully!';
        message.success(successMessage);
        // Add solicitation to ACE folder using solicitationNum first, then ID fallback
        await addToAceFolder(items.solicitationNum || items.id, auth.id);

        // Hint other tabs/pages to refresh counts (listen for this if desired)
        try {
          window.localStorage.setItem('aceFolderUpdatedAt', String(Date.now()));
          window.dispatchEvent(new Event('storage'));
        } catch (e) {
          // no-op
        }
      } else {
        console.error('ACE API Error:', response.data);
        // Extract error message with details if available
        const errorMsg = response.data.error || 'Unknown error';
        const errorDetails = response.data.details || '';
        const fullErrorMessage = errorDetails
          ? `${errorMsg}: ${errorDetails}`
          : errorMsg;
        message.error(`Failed to send solicitation to ACE: ${fullErrorMessage}`);
      }
    } catch (error) {
      console.error('Error sending to ACE:', error);

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        console.error('Error Response:', {
          status,
          data: errorData,
          headers: error.response.headers
        });

        // Handle specific HTTP status codes per Salesforce API documentation
        let errorMessage = 'Unknown error';
        let errorDetails = '';

        // Extract error and details from response
        if (errorData) {
          errorMessage = errorData.error || errorData.message || 'Unknown error';
          errorDetails = errorData.details || '';

          // Check if details contain Salesforce-specific errors
          if (errorDetails) {
            if (errorDetails.includes('403') || errorDetails.includes('Forbidden')) {
              errorMessage = 'Salesforce authentication failed. The backend service may need to refresh its Salesforce credentials. Please contact support.';
            } else if (errorDetails.includes('401') || errorDetails.includes('Unauthorized')) {
              errorMessage = 'Salesforce authentication expired. Please contact support to refresh the connection.';
            }
          }
        }

        if (status === 400) {
          errorMessage = errorMessage || 'Missing required fields. Please check your data and try again.';
        } else if (status === 401 || status === 403) {
          errorMessage = errorMessage || 'Authentication failed. Please log out and log back in, then try again.';
        } else if (status === 409) {
          errorMessage = errorMessage || 'Account/Contact conflict. Please contact support.';
        } else if (status === 500) {
          // For 500 errors, check if it's a Salesforce error wrapped in the response
          if (errorDetails && (errorDetails.includes('Salesforce') || errorDetails.includes('403') || errorDetails.includes('401'))) {
            // Error message already set above from errorData
          } else {
            errorMessage = errorMessage || 'Server error. Please try again later or contact support.';
          }
        } else {
          errorMessage = errorMessage || error.response.statusText || 'Unknown error';
        }

        // Include details if available and not already in the message
        const fullErrorMessage = errorDetails && !errorMessage.includes(errorDetails)
          ? `${errorMessage} (${errorDetails})`
          : errorMessage;

        message.error(`Error sending solicitation to ACE: ${fullErrorMessage}`);
      } else if (error.request) {
        // Request was made but no response received
        console.error('No response received:', error.request);
        message.error('Network error. Please check your connection and try again.');
      } else {
        // Error setting up the request
        console.error('Request setup error:', error.message);
        message.error('Error sending solicitation to ACE. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOnClick = () => {
    sendToAce();
  }

  // Debug logging
  console.log('ACE Button Debug:', {
    isFeatureEnabled: isFeatureEnabled('ENABLE_ACE'),
    subscriptionLevel,
    items: items ? 'present' : 'missing',
    auth: auth ? 'present' : 'missing',
    jwtToken: auth && auth.jwtToken ? 'present' : 'missing',
    authKeys: auth ? Object.keys(auth) : 'no auth object'
  });

  if (isFeatureEnabled('ENABLE_ACE') && subscriptionLevel === 'ICON Pro') {
    return (
      <Button
        id="sndAce"
        onClick={handleOnClick}
        loading={loading}
        type="primary"
        style={{
          background: '#ff7c52',
          border: '#ff7c52',
          borderRadius: '6px',
        }}
      >
        Send to ACE
      </Button>
    )
  }
  return (<span />);
}

AceButton.propTypes = {
  auth: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    jwtToken: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    cageCode: PropTypes.string,
  }).isRequired,
  items: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    solicitationNum: PropTypes.string,
    nomenclature: PropTypes.string,
    nsn: PropTypes.string,
    partNumber: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    delivery: PropTypes.string,
    issueDate: PropTypes.string,
    closes: PropTypes.string,
    returnDate: PropTypes.string,
    deliveryByFobDestination: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    fob: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    delivery_by_fob_destination: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    wsdcs: PropTypes.arrayOf(
      PropTypes.shape({
        wsName: PropTypes.string,
        WS_NAME: PropTypes.string,
        wsdc: PropTypes.string,
      })
    ),
    castingType: PropTypes.string,
    srva: PropTypes.string,
    refNbrs: PropTypes.arrayOf(PropTypes.object),
    um: PropTypes.string,
    estValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    prgid: PropTypes.string,
    setAside: PropTypes.string,
    packagingInfo: PropTypes.string,
    amc: PropTypes.string,
    techInfo: PropTypes.string,
    procurementHistory: PropTypes.arrayOf(
      PropTypes.shape({
        awardedDate: PropTypes.string,
        unitCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    solicitationTab: PropTypes.arrayOf(
      PropTypes.shape({
        standardCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    purchaseRequest: PropTypes.shape({
      nomenclature: PropTypes.string,
      nsnPart: PropTypes.string,
      number: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      setAside: PropTypes.string,
      naics: PropTypes.string,
      NAICS: PropTypes.string,
      NAICS_CODE: PropTypes.string,
    }),
    // EBS Material fields from itemDescription (from universal details query, not itemDescInfo)
    itemDescription: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object),
    ]),
    // itemDescInfo is a string from the solicitation query (just text description)
    itemDescInfo: PropTypes.string,
    // Item details from NSN_MASTER
    itemDetails: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object),
    ]),
    // EBS Packaging data
    ebsPackaging: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object),
    ]),
    // Recent solicitations (for NAICS)
    recentSolicitations: PropTypes.arrayOf(
      PropTypes.shape({
        agency: PropTypes.string,
        buyer: PropTypes.string,
        closes: PropTypes.string,
        phoneEMail: PropTypes.string,
        quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        small: PropTypes.string,
        solicitation: PropTypes.string,
        status: PropTypes.string,
        um: PropTypes.string,
        naics: PropTypes.string,
        NAICS: PropTypes.string,
        NAICS_CODE: PropTypes.string,
      })
    ),
    // Direct field access (fallback)
    hmic0865: PropTypes.string,
    placeInspnCd: PropTypes.string,
    naics: PropTypes.string,
    NAICS: PropTypes.string,
    NAICS_CODE: PropTypes.string,
    naicsCode: PropTypes.string,
  }).isRequired,
};

export default AceButton;


// WEBPACK FOOTER //
// ./src/pages/SolicitationDetail/AceButton.jsx